import { IconCheck, IconRefresh } from "@tabler/icons-react";
import { Button } from "@/components/lilia-ui/button";
import { usePersonalityStore } from "@/store/personalityStore";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { cn } from "@/lib/cn";
import { testIds } from "@/testIds";
import { useRefreshSequence } from "./useRefreshSequence";
import "./refreshButton.css";

/**
 * RefreshButton — one component for every refresh (saved-moment handoff §5),
 * in the same family as the saved moment:
 *
 *   idle ↻ → pressed (0.9×, 80 ms) → working (spins; after 150 ms, held
 *   ≥ 400 ms) → done (green ✓ on a subtle-green fill, 1 s) → idle.
 *   Failed: red-bordered ! and "Couldn't refresh · Try again". No shake.
 *
 * 32 × 32 on IconBtn's ground (lilia-ui Button, subtle, icon-sm); stays
 * enabled while working (pressing again restarts the job); aria-busy while
 * working. Fun adds three green sparks on done; refresh buttons never get
 * characters.
 *
 * Testids: the group is `refresh-button` with `data-state`
 * (idle · working · done · failed). The button inside keeps the place's own
 * testid (`pdf-refresh-button`, `validation-rerun`, …), so every existing
 * selector still clicks the same thing.
 */
export interface RefreshButtonProps {
  /** The refresh. A returned promise is tracked (reject → failed). */
  onRefresh: () => unknown;
  /** Accessible name and tooltip, e.g. "Refresh PDF". */
  label: string;
  /** The place's own testid, on the button itself. */
  testId?: string;
  /** For work the click only starts: true while it runs (see useRefreshSequence). */
  busy?: boolean;
  /** Timestamp variant, where freshness matters: "Preview · updated 10:42". */
  updatedAt?: Date | null;
  /** What was refreshed, for the timestamp ("Preview"). */
  subject?: string;
  /** A visible word beside the icon ("Re-run"), for places that had one. */
  text?: string;
  disabled?: boolean;
  iconSize?: number;
  className?: string;
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function RefreshButton({
  onRefresh,
  label,
  testId = testIds.refreshButton.trigger,
  busy,
  updatedAt,
  subject,
  text,
  disabled,
  iconSize = 16,
  className,
}: RefreshButtonProps) {
  const { phase, pressId, run } = useRefreshSequence(onRefresh, busy);
  const fun = usePersonalityStore((s) => s.personality === "fun");
  const reduced = usePrefersReducedMotion();
  const working = phase === "working";

  return (
    <span
      data-testid={testIds.refreshButton.root}
      data-state={phase}
      className={cn("rb-root", className)}
    >
      <Button
        type="button"
        variant="subtle"
        size={text ? "xs" : "icon-sm"}
        data-testid={testId}
        data-state={phase}
        aria-label={text ? undefined : label}
        title={label}
        aria-busy={working || undefined}
        disabled={disabled}
        onClick={run}
        className={cn("rb-button", text && "rb-button--text", `rb-button--${phase}`)}
      >
        <span key={pressId} className="rb-press" data-pressed={pressId > 0 || undefined}>
          <span className="rb-icon">
            {phase === "done" ? (
              <IconCheck size={iconSize} stroke={2.6} className="rb-done" aria-hidden />
            ) : phase === "failed" ? (
              <span className="rb-failed" aria-hidden>!</span>
            ) : working && reduced ? (
              <span className="rb-static-ring" aria-hidden />
            ) : (
              <IconRefresh size={iconSize} stroke={2} className={cn(working && "rb-spin")} aria-hidden />
            )}
            {phase === "done" && fun && (
              <>
                <span className="rb-spark rb-spark--1" aria-hidden />
                <span className="rb-spark rb-spark--2" aria-hidden />
                <span className="rb-spark rb-spark--3" aria-hidden />
              </>
            )}
          </span>
        </span>
        {text && <span className="rb-text">{text}</span>}
      </Button>

      {phase === "failed" ? (
        <span className="rb-note" role="status">
          Couldn&rsquo;t refresh ·{" "}
          <button type="button" className="rb-retry" data-testid={testIds.refreshButton.retry} onClick={run}>
            Try again
          </button>
        </span>
      ) : working && reduced ? (
        <span className="rb-note" role="status">Refreshing…</span>
      ) : updatedAt || subject ? (
        <span className="rb-note rb-note--muted" data-testid={testIds.refreshButton.timestamp}>
          {subject ?? "Updated"}
          {updatedAt ? ` · updated ${formatTime(updatedAt)}` : ""}
        </span>
      ) : null}
    </span>
  );
}
